import React from 'react';
import { Icon } from '../icon/Icon.jsx';

/* Trigger renders as FormField's control with a chevron-down. Menu on
   --bg-raised with --shadow-overlay plus a mandatory 1px border (R4, because
   light --bg-surface and --bg-raised are both white). 32px rows, selected row
   --accent-subtle with a check. Multi-select renders removable chips inside the
   trigger, wrapping to at most two lines before collapsing to "+n more". */

export function Select({ options = [], value, values = [], onChange, multiple = false, placeholder = 'Select…', open: openProp, disabled, width = 240, maxChips = 4, style }) {
  const [open, setOpen] = React.useState(!!openProp);
  React.useEffect(() => { if (openProp !== undefined) setOpen(openProp); }, [openProp]);
  const opts = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const selected = multiple ? values : (value != null ? [value] : []);
  const shown = selected.slice(0, maxChips);
  const overflow = selected.length - shown.length;

  const pick = (v) => {
    if (!onChange) return;
    if (multiple) onChange(selected.includes(v) ? selected.filter((x) => x !== v) : selected.concat(v));
    else { onChange(v); setOpen(false); }
  };

  return (
    <div style={{ position: 'relative', width, ...style }}>
      <button type="button" disabled={disabled} onClick={() => setOpen(!open)} className="jmt-select-trigger"
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-4)', width: '100%', minHeight: 'var(--control-h, 32px)',
          padding: multiple && selected.length ? '4px 8px' : '0 8px 0 12px',
          background: 'var(--bg-inset)', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-control)', color: 'var(--text-primary)',
          fontFamily: 'var(--font-ui)', fontSize: 'var(--body-sm-size)', cursor: 'pointer', textAlign: 'left',
          flexWrap: 'wrap',
        }}>
        <span style={{ flex: 1, display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'center', minWidth: 0 }}>
          {selected.length === 0 ? <span style={{ color: 'var(--text-tertiary)' }}>{placeholder}</span> : null}
          {multiple ? shown.map((v) => (
            <span key={v} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 6px',
              background: 'var(--bg-raised)', border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-pill)', fontSize: 'var(--caption-size)',
            }}>{v}<Icon name="x" size={11} style={{ opacity: .7 }} /></span>
          )) : (selected.length ? <span>{(opts.find((o) => o.value === selected[0]) || {}).label || selected[0]}</span> : null)}
          {overflow > 0 ? <span style={{ fontSize: 'var(--caption-size)', color: 'var(--text-secondary)' }}>+{overflow} more</span> : null}
        </span>
        <Icon name="chevron-down" size={16} style={{ color: 'var(--text-tertiary)' }} />
      </button>

      {open ? (
        <div role="listbox" style={{
          position: 'absolute', zIndex: 40, top: 'calc(100% + 4px)', left: 0, minWidth: '100%',
          background: 'var(--bg-raised)', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-control)', boxShadow: 'var(--shadow-overlay)', padding: 'var(--space-4) 0',
        }}>
          {opts.map((o) => {
            const on = selected.includes(o.value);
            return (
              <div key={o.value} role="option" aria-selected={on} onClick={() => pick(o.value)} className="jmt-select-row"
                style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-8)', height: 32, padding: '0 var(--space-12)',
                  background: on ? 'var(--accent-subtle)' : 'transparent',
                  color: on ? 'var(--accent)' : 'var(--text-primary)',
                  fontSize: 'var(--body-sm-size)', cursor: 'pointer',
                }}>
                <span style={{ width: 16 }}>{on ? <Icon name="check" size={14} /> : null}</span>
                <span style={{ flex: 1 }}>{o.label}</span>
                {o.hint ? <span style={{ fontSize: 'var(--caption-size)', color: 'var(--text-tertiary)' }}>{o.hint}</span> : null}
              </div>
            );
          })}
        </div>
      ) : null}
      <style>{`
        .jmt-select-trigger:focus-visible{outline:none;border-color:var(--border-strong);box-shadow:var(--focus-ring)}
        .jmt-select-row:hover{background:var(--bg-surface)}
      `}</style>
    </div>
  );
}
